import { useCallback, useEffect, useState } from "react";
import { FormComponent } from "../../component/form/FormComponent";
import TableComponent from "../../component/form/TableComponent";
import { useAuth } from "../../context/authContext";
import { useLoading } from "../../context/loadingContext";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../context/notificationContext";
import { isSuccessCode } from "../../helpers/apiResponse";
import * as ReportTypeAPI from "../../api/reportType";
import { Field } from "../../types/FromComponent.types";
import { Column, TableButtons } from "../../types/TableComponent.types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrash } from "@fortawesome/free-solid-svg-icons";
import { LoadingScreenLocal } from "../other/LoadingScreen";
import "./ReportTypeManageScreen.css";

type ReportTypeItem = {
    id: number;
    name: string;
};

const toReportType = (item: Record<string, unknown>): ReportTypeItem => ({
    id: Number(item.id ?? item.report_type_id ?? 0),
    name: String(item.name ?? item.report_type_name ?? ""),
});

const ReportTypeManageScreen: React.FC = () => {
    const { association } = useAuth();
    const { loading, setLoadingStatus } = useLoading();
    const { addNotification } = useNotification();
    const nav = useNavigate();
    const associationId = String(association.id);

    const [reportTypes, setReportTypes] = useState<ReportTypeItem[]>([]);

    const handleApiError = useCallback((requestId: string, errorCode: string) => {
        nav(`/500?process_id=${requestId}&error_code=${errorCode}`);
    }, [nav]);

    const loadReportTypes = useCallback(async () => {
        const response = await ReportTypeAPI.list(associationId);
        if (!isSuccessCode(response.error_code)) {
            return handleApiError(response.request_id, response.error_code);
        }

        const mapped = (Array.isArray(response.data) ? response.data : [])
            .map((item: Record<string, unknown>) => toReportType(item))
            .filter((item) => item.id > 0);
        setReportTypes(mapped);
    }, [associationId, handleApiError]);

    useEffect(() => {
        setLoadingStatus(true);
        loadReportTypes().finally(() => setLoadingStatus(false));
    }, [loadReportTypes, setLoadingStatus]);

    const handleCreateReportType = async (data: { [key: string]: string }) => {
        if (!data.name?.trim()) {
            return addNotification("Validation error", "Report type name is required", "error");
        }

        setLoadingStatus(true);
        const response = await ReportTypeAPI.create({ name: data.name.trim() }, associationId);
        if (!isSuccessCode(response.error_code)) {
            setLoadingStatus(false);
            return handleApiError(response.request_id, response.error_code);
        }

        addNotification("Created", "Report type created successfully", "success");
        await loadReportTypes();
        setLoadingStatus(false);
    };

    const handleDeleteReportType = async (reportTypeId: number) => {
        setLoadingStatus(true);
        const response = await ReportTypeAPI.remove(reportTypeId);
        if (!isSuccessCode(response.error_code)) {
            setLoadingStatus(false);
            return handleApiError(response.request_id, response.error_code);
        }

        addNotification("Deleted", "Report type deleted successfully", "success");
        await loadReportTypes();
        setLoadingStatus(false);
    };

    const createFields: Field[] = [
        { name: "name", placeholder: "Report type name", type: "text" },
    ];

    const columns: Column[] = [
        { header: "ID", accessor: "id" },
        { header: "Name", accessor: "name" },
    ];

    const buttons: TableButtons[] = [
        {
            button: <FontAwesomeIcon icon={faPen} title="Edit" />,
            bc: "green",
            placeholder: "Edit",
            action: { param: "id", function: (id: number) => nav(`/report/edit/${id}`) }
        },
        {
            button: <FontAwesomeIcon icon={faTrash} title="Delete" />,
            bc: "red",
            placeholder: "Delete",
            action: { param: "id", function: handleDeleteReportType }
        },
    ];

    if (loading) {
        return <LoadingScreenLocal />;
    }

    return (
        <div className="report-type-manage-screen">
            <TableComponent
                data={reportTypes}
                columns={columns}
                buttons={buttons}
            />
            <FormComponent
                title="Create report type"
                fields={createFields}
                onSubmit={handleCreateReportType}
                button={{ name: "Create" }}
            />
        </div>
    );
};

export default ReportTypeManageScreen;
